import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
/**
 * Verbal Fluency — vue React staircase + debrief (cyanotype).
 *
 * Rendu : prompt centré (lettre ou catégorie) + countdown timer, champ
 * texte, liste des mots tapés (chips), bouton Ajouter + Terminer round.
 */
import { useCallback, useMemo, useState, useSyncExternalStore, } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DebriefShell, useGameShellFromContext } from '@labrain/game-shell';
import { staircaseConfig } from './params';
export function VerbalFluencyStaircaseView({ adapter, level: _level, onAbort, }) {
    const { t } = useTranslation('games');
    const subscribe = useCallback((notify) => adapter.subscribe(() => notify()), [adapter]);
    const getSnapshot = useCallback(() => adapter.getState(), [adapter]);
    const adapterState = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
    const shell = useGameShellFromContext();
    const [draft, setDraft] = useState('');
    const [shake, setShake] = useState(false);
    const stim = adapterState.stim;
    const trialDisplay = shell.snapshot.staircase.trialIdx + 1;
    const promptLabel = useMemo(() => {
        if (!stim)
            return '';
        if (stim.prompt.mode === 'phonemic') {
            const fallback = `Mots commençant par ${stim.prompt.key}`;
            return t('verbal_fluency.phonemic.prompt', {
                letter: stim.prompt.key,
                defaultValue: fallback,
            });
        }
        return t(stim.prompt.labelKey, {
            defaultValue: stim.prompt.key,
        });
    }, [stim, t]);
    const handleSubmit = useCallback(() => {
        if (draft.trim().length < 2)
            return;
        const result = adapter.addWord(draft);
        if (result.accepted) {
            setDraft('');
        }
        else {
            setShake(true);
            setTimeout(() => setShake(false), 350);
        }
    }, [adapter, draft]);
    return (_jsxs("div", { className: "flex flex-1 flex-col items-center gap-s4 py-s4", children: [_jsxs("div", { className: "flex w-full items-center justify-between text-[12px] text-cyan-text-secondary", children: [_jsxs("span", { className: "font-mono uppercase tracking-[0.20em] text-cyan-text-faint", children: ["Round ", Math.min(trialDisplay, staircaseConfig.maxTrials), "/", staircaseConfig.maxTrials] }), _jsxs("span", { className: "font-mono uppercase tracking-[0.20em] text-cyan-network", children: [Math.ceil(adapterState.remainingMs / 1000), "s"] }), _jsx("button", { type: "button", onClick: () => {
                            shell.abort('user');
                            onAbort();
                        }, className: "border border-cyan-border-faint px-s3 py-s1 text-[11px] text-cyan-text-secondary hover:border-cyan-border", children: "Arr\u00EAter" })] }), stim && (_jsxs("div", { className: "flex flex-col items-center gap-s2", children: [_jsx("p", { className: "font-mono text-[10px] uppercase tracking-[0.20em] text-cyan-text-faint", children: stim.prompt.mode === 'phonemic' ? 'Lettre' : 'Catégorie' }), _jsx("h2", { className: "font-display text-[32px] font-medium tracking-cy-display text-cyan-text", children: promptLabel })] })), adapterState.subPhase === 'awaiting_input' && (_jsxs("div", { className: "flex w-full max-w-[340px] flex-col gap-s3", children: [_jsx("input", { type: "text", value: draft, onChange: (e) => setDraft(e.target.value), onKeyDown: (e) => {
                            if (e.key === 'Enter' || e.key === 'Tab') {
                                e.preventDefault();
                                handleSubmit();
                            }
                        }, placeholder: "un mot...", className: [
                            'w-full border border-cyan-border bg-cyan-surface px-s4 py-s3 text-[15px] text-cyan-text focus:border-cyan-core focus:outline-none',
                            shake ? 'animate-pulse border-cyan-media' : '',
                        ].join(' '), autoFocus: true }), _jsxs("div", { className: "flex justify-between", children: [_jsxs("span", { className: "font-mono text-[10px] uppercase tracking-[0.18em] text-cyan-text-faint", children: [adapterState.words.length, " mots"] }), _jsx("button", { type: "button", onClick: handleSubmit, disabled: draft.trim().length < 2, className: "inline-flex h-10 items-center justify-center bg-cyan-core px-s5 font-display text-[13px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network disabled:bg-cyan-border-faint disabled:text-cyan-text-secondary disabled:opacity-40", children: "Ajouter" })] })] })), _jsxs("div", { className: "flex w-full max-w-[340px] flex-wrap justify-center gap-s2", children: [adapterState.words.map((w, i) => (_jsx("span", { className: "border border-cyan-network/30 bg-cyan-network/15 px-s3 py-s1 text-[11px] text-cyan-text", children: w }, `${w}-${i}`))), adapterState.words.length === 0 &&
                        adapterState.subPhase === 'awaiting_input' && (_jsx("span", { className: "text-[11px] text-cyan-text-secondary", children: "Tape un mot puis Entr\u00E9e." }))] }), adapterState.subPhase === 'awaiting_input' && (_jsx("button", { type: "button", onClick: () => adapter.finishRound(), className: "mt-auto text-[12px] text-cyan-text-secondary underline-offset-4 hover:underline", children: "Terminer le round" })), adapterState.subPhase === 'feedback' && (_jsx("span", { className: [
                    'font-display text-[36px] font-medium',
                    adapterState.feedback === 'correct'
                        ? 'text-cyan-core'
                        : 'text-cyan-media',
                ].join(' '), children: adapterState.feedback === 'correct' ? '✓' : '✗' }))] }));
}
export function VerbalFluencyDebriefView({ level: _level, onAbort: _onAbort, }) {
    const shell = useGameShellFromContext();
    const staircase = shell.snapshot.staircase;
    const trialsCompleted = staircase.trialIdx;
    const fallback = !!staircase.fallback;
    // Médiane des wordCount par round.
    const wordCounts = staircase.trialLog.map((tt) => {
        const raw = tt.response.rawInput;
        return raw?.wordCount ?? 0;
    });
    const sortedCounts = [...wordCounts].sort((a, b) => a - b);
    const median = sortedCounts.length === 0
        ? 0
        : sortedCounts[Math.floor(sortedCounts.length / 2)] ?? 0;
    // Breakdown phonemic vs semantic.
    const phonemicCounts = [];
    const semanticCounts = [];
    for (const tt of staircase.trialLog) {
        const raw = tt.response.rawInput;
        if (!raw || typeof raw.wordCount !== 'number')
            continue;
        if (raw.mode === 'phonemic')
            phonemicCounts.push(raw.wordCount);
        else if (raw.mode === 'semantic')
            semanticCounts.push(raw.wordCount);
    }
    const avg = (arr) => arr.length === 0
        ? '—'
        : Math.round((arr.reduce((a, b) => a + b, 0) / arr.length) * 10) / 10;
    const recommendation = (() => {
        if (trialsCompleted === 0) {
            return 'Reviens demain : la fluence verbale progresse en répétant.';
        }
        if (median >= 12) {
            return 'Récupération lexicale rapide. Tente un palier supérieur.';
        }
        if (median >= 8) {
            return 'Production stable. Reviens demain pour fluidifier davantage.';
        }
        return 'Fluence en construction. La régularité paye.';
    })();
    return (_jsx(DebriefShell, { title: "Fluence verbale", scoreLabel: "Mots produits (m\u00E9dian)", scoreValue: median, fallbackBadge: fallback, metrics: [
            { label: 'Rounds', value: trialsCompleted },
            { label: 'Phonemic moy.', value: avg(phonemicCounts) },
            { label: 'Sémantique moy.', value: avg(semanticCounts) },
        ], recommendation: recommendation, scientificCaveat: _jsx("span", { className: "block text-[10px] leading-snug text-cyan-text-faint", children: "Paradigme Verbal Fluency Test (FAS + s\u00E9mantique). Cf. Lezak, Howieson, Bigler & Tranel (2012), Neuropsychological Assessment, 5e \u00E9d., Oxford University Press (ISBN 978-0195395525) ; Shao, Janse, Visser & Meyer (2014), Frontiers in Psychology 5: 772, doi:10.3389/fpsyg.2014.00772. Score non \u00E9quivalent \u00E0 un diagnostic." }), ctaReplay: _jsx("button", { type: "button", onClick: () => window.location.reload(), className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: "Rejouer" }), ctaBack: _jsx(Link, { to: "/library", className: "inline-flex h-12 w-full items-center justify-center border border-cyan-border bg-cyan-surface font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-text transition active:scale-[0.985] hover:bg-cyan-bg", children: "Choisir un autre jeu" }) }));
}
//# sourceMappingURL=staircaseView.js.map